import { Box, Button, Dialog, DialogActions, DialogContent, Grid, IconButton, Typography, useTheme } from "@mui/material";
import { ApplicationProps, DataMaster, dataTypeMasters, NomineeType } from "../../../redux-store/types/api-types";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import { aadhaarRegexLastFour, addressRegex, alphabetRegex, alphaNumeric, drivingLicenseRegex, emailRegex, indianPin, individualPanRegex, multipleWordsRegex, passportRegex, phoneRegExp, spacesStringRegex, stringRegex } from "../../../utils/regex";
import * as yup from 'yup';
import { futureAge, getCountryCodes, getUniqueIdentityText, isMinor, maxAge } from "../../../utils/utilityFunctions";
import { idProofLabelsMasters, IdProofsMasters } from "../../../utils/constant";
import { DialogCloseIcon } from "../../../assets/SvgIcons";
import { Form, Formik, validateYupSchema, yupToFormErrors } from "formik";
import { MFSubmitButton, MFTextField } from "../../../lib/formik";
import MFSelectField from "../../../lib/formik/SelectField";
import { TextDatePicker } from "../../onboarding/DatePickerWithTextFeild";
import { SubHeading } from "../../investors/components";
import { SearchableSelect } from "../../../lib/formik/searchSelectField";
import { updateNominee } from "../CreateJointHolder";
import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { useEffect, useState } from "react";
import { CountryCodesDropDown } from "../../commonComponents";

const addNomineeSchema = (applications?: ApplicationProps[], currencyCode = "INR", company = "WMS", mainValueObj?: any) => yup.object().shape({
    nomineeName: yup.string()
        .required('Nominee Name is required')
        .min(2, 'Nominee Name must be at least 2 characters')
        .max(50, 'Nominee Name must not exceed 50 characters')
        .matches(multipleWordsRegex, 'Numbers and Special Characters not allowed'),
    nomineeRelationship: yup.string().when(['Relationship', 'isActive'], {
        is: (Relationship: string | null, isActive: boolean) =>
            isActive && `${Relationship ?? ''}` === 'others',
        then: yup
            .string()
            .matches(stringRegex, 'Special characters and numbers not allowed')
            .required('Please Specify Relationship'),
    }),
    Relationship: yup
        .string()
        .nullable()
        .when('isActive', {
            is: (isActive: boolean) => isActive,
            then: yup
                .string()
                .nullable()
                .required('Please select relationship'),
        }),
    dateOfBirth: yup
        .string()
        .nullable()
        .test('dateOfBirth', (value, context) => {
            const fieldValue = value === undefined ? '' : value;
            const { path, createError, parent } = context;
            if (!fieldValue && parent.isActive) {
                return createError({
                    message: 'Date of Birth is required',
                    path,
                });
            }
            if (futureAge(fieldValue || '') && parent.isActive) {
                return createError({
                    message: 'Invalid Date',
                    path,
                });
            }
            if (maxAge(fieldValue || '') && parent.isActive) {
                return createError({
                    message: 'Age should be less than 125',
                    path,
                });
            }
            return true;
        }),
    nomineeGuardianPan: yup
        .string()
        .nullable()
        .when(['dateOfBirth', 'isActive'], {
            is: (dateOfBirth: string | null, isActive: boolean) =>
                isActive && isMinor(dateOfBirth || ''),
            then: yup
                .string()
                .nullable()
                .required('Guardian PAN is required')
                .test('nomineeGuardianPan', (value, context) => {
                    const {
                        options: { context: optionsContext },
                        createError,
                        path,
                        parent,
                    } = context;
                    const applicantsPan = applications?.map((application: ApplicationProps) => application?.applicants?.map((applicant) => applicant.panNo) || []).flat() || [];
                    if (currencyCode?.toLowerCase() === "inr") {
                        if (!individualPanRegex.test(value ?? "")) {
                            return createError({
                                message: 'Only individual PANs are allowed',
                                path,
                            });
                        }
                    }

                    if (value && applicantsPan.length && applicantsPan?.includes(value)) {
                        return createError({
                            message: `Applicant ${getUniqueIdentityText(company)?.pan} not allowed to enter as Guardian ${getUniqueIdentityText(company)?.pan}`,
                            path,
                        });
                    }
                    return true;
                }),
        }),
    idProofType: yup
        .string()
        .nullable().oneOf(IdProofsMasters, 'Invalid ID Proof Type')
        .required(`ID Proof Type is required`),
    idProofValue: yup
        .string()
        .required("ID Number is Required")
        .test('idProofValue', (value, context) => {
            const { path, createError } = context;
            const { parent } = context;
            const idProofType = context.parent.idProofType;

            const applicantsPan = applications?.map((application: ApplicationProps) => application?.applicants?.map((applicant) => applicant.panNo) || []).flat() || [];

            const activeNominees = mainValueObj?.nomineeDetails?.filter((nominee: updateNominee) => nominee.isActive && nominee.sno !== parent.sno);

            if (!value) return true;
            if (value) {
                switch (idProofType) {
                    case 'PAN Card':
                        // 1. Format check
                        if (!individualPanRegex.test(value)) {
                            return createError({ message: 'Only Individual PAN Allowed', path });
                        }

                        // 2. Prevent nominee PAN = any applicant PAN
                        if (applicantsPan.includes(value)) {
                            return createError({
                                message: `Applicant ${getUniqueIdentityText(company)?.pan} not allowed to enter as nominee ${getUniqueIdentityText(company)?.pan}`,
                                path,
                            });
                        }
                        // 3. Check for duplicate nominee PAN among active nominees
                        if (activeNominees?.filter((n: any) => n.idProofValue === value && n.idProofType === 'PAN Card').length > 0) {
                            return createError({
                                message: `There is already same ${getUniqueIdentityText(company)?.pan} for a nominee associated with this application`,
                                path,
                            });
                        }

                        // 4. Guardian PAN should not match nominee PAN
                        if (parent.nomineeGuardianPan && parent.nomineeGuardianPan === value && parent.idProofType === 'PAN Card' && isMinor(parent.dateOfBirth ? parent.dateOfBirth : '')) {
                            return createError({
                                message: `Nominee ${getUniqueIdentityText(company)?.pan} should not be same as Guardian ${getUniqueIdentityText(company)?.pan}`,
                                path,
                            });
                        }

                        break;
                    case 'Aadhaar Card':
                        if (!aadhaarRegexLastFour.test(value)) {
                            return createError({
                                message: 'Invalid Aadhaar Number (last 4 digits only)',
                                path,
                            });
                        }
                        break;
                    case 'Driving License':
                        if (!drivingLicenseRegex.test(value)) {
                            return createError({
                                message: 'Invalid Driving License Number (max 20 digit)',
                                path,
                            });
                        }
                        break;
                    case 'Passport':
                        if (!passportRegex.test(value)) {
                            return createError({
                                message: 'Invalid Passport Number (max 9 Digit)',
                                path,
                            });
                        }
                        break;
                    default:
                        return createError({
                            message: 'Invalid ID Proof Type',
                            path,
                        });
                }
            }
            return true;
        }),
    mobile: yup
        .string()
        .nullable()
        .test('mobile', (value, context) => {
            const { createError, path, parent } = context;
            // if (parent.isActive === false) return true;
            const codesList = getCountryCodes()
                .map((codes) => {
                    if (codes.label === parent.countryNameAndCode) {
                        return codes.countryCode;
                    }
                })
                .filter((ele) => ele)
                .toString();
            if (!value) {
                return createError({
                    message: 'Mobile is required',
                    path,
                });
            }
            if (parent.countryNameAndCode === 'India: +91' && !phoneRegExp.test(value)) {
                return createError({
                    message: 'Invalid Mobile number',
                    path,
                });
            }
            if (!isValidPhoneNumber(value, codesList as CountryCode)) {
                return createError({
                    message: 'Invalid Mobile number',
                    path,
                });
            }

            return true;
        }),
    countryNameAndCode: yup
        .string()
        .nullable()
        .test('countryNameAndCode', (value, context) => {
            const { createError, path, parent } = context;
            // if (parent.isActive === false) return true;
            const codesList = getCountryCodes()
                .map((codes) => {
                    if (codes.label === value) {
                        return codes.countryCode;
                    }
                })
                .filter((ele) => ele)
                .toString();
            if (!value) {
                return createError({
                    message: 'Country Code is required',
                    path,
                });
            }
            if (parent.mobile && !isValidPhoneNumber(parent.mobile, codesList as CountryCode)) {
                return createError({
                    message: 'Invalid Mobile number',
                    path,
                });
            }

            return true;
        }),
    email: yup
        .string()
        .nullable()
        .matches(emailRegex, 'invalid Email ID')
        .required('Email ID is required'),
    address1: yup
        .string()
        .nullable()
        .matches(
            addressRegex,
            "invalid address line 1 format [special characters are not allowed except -/',&()#:.]"
        )
        .required('Address Line 1 is required')
        .max(40, 'Address Line 1 should not be more than 40 characters'),
    address2: yup
        .string()
        .nullable()
        .matches(
            addressRegex,
            "invalid address line 2 format [special characters are not allowed except -/',&()#:.]"
        )
        .max(40, 'Address Line 2 should not be more than 40 characters'),
    // .required('Address Line 2 is required'),
    address3: yup
        .string()
        .nullable()
        .matches(
            addressRegex,
            "invalid address line 3 format [special characters are not allowed except -/',&()#:.]"
        )
        .max(40, 'Address Line 3 should not be more than 40 characters'),

    // .required('Address Line 3 is required'),
    pincode: yup
        .string()
        .nullable()
        .test('pincode', function (value, context) {
            const fieldValue = value ?? '';
            const { createError, path, parent } = context;

            if (!fieldValue) {
                return createError({
                    message: 'Pincode is required',
                    path,
                });
            }

            if (parent.country?.toLowerCase() === 'india' && !indianPin.test(fieldValue)) {
                return createError({
                    message: 'Pincode should be 6 digit code',
                    path,
                });
            }

            if (parent.country?.toLowerCase() !== 'india' && !alphaNumeric.test(fieldValue)) {
                return createError({
                    message: 'Invalid Pincode',
                    path,
                });
            }
            return true;
        }),
    city: yup
        .string()
        .nullable()
        .matches(spacesStringRegex, 'Special characters not allowed')
        .required('City is required'),
    otherCity: yup
        .string()
        .nullable()
        .matches(spacesStringRegex, 'Special characters not allowed')
        .test('otherCity', 'City is required', (value, context) => {
            const { parent } = context;
            if (`${parent?.city ?? ''}`.toLowerCase() === 'others' && !value) {
                return context.createError({ message: 'City is required', path: context.path });
            }
            return true;
        }),
    state: yup
        .string()
        .nullable()
        .matches(spacesStringRegex, 'Special characters not allowed')
        .required('State is required'),
    otherState: yup
        .string()
        .nullable()
        .matches(spacesStringRegex, 'Special characters not allowed')
        .test('otherState', 'State is required', (value, context) => {
            const { parent } = context;
            if (`${parent?.state ?? ''}`.toLowerCase() === 'others' && !value) {
                return context.createError({ message: 'State is required', path: context.path });
            }
            return true;
        }),
    country: yup
        .string()
        .nullable()
        .required('Country is required'),
});

const newNominee: updateNominee = {
    nomineeName: '',
    nomineeRelationship: '',
    // nomineePan: '',
    idProofType: '',
    idProofValue: '',
    mobile: '',
    countryNameAndCode: 'India: +91',
    countryCode: '+91',
    email: '',
    address1: '',
    address2: '',
    address3: '',
    pincode: '',
    city: '',
    otherCity: '',
    state: '',
    otherState: '',
    country: '',
    dateOfBirth: null,
    nomineePercentage: 0,
    guardianName: '',
    nomineeGuardianPan: '',
    isActive: true,
    Relationship: '',
    sno: 0,
    addressSameAsApplicant: false,
    addressSameAsFirstNominee: false,
};
export const AddNomineePopup = ({
    open,
    onClose,
    value,
    setFieldValue: setNomineeFieldValue,
    mdmsCountriesList,
    applications,
    index
}: {
    open: boolean;
    onClose: () => void;
    value: any;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    mdmsCountriesList: DataMaster;
    applications: ApplicationProps[]
    index?: number
}): JSX.Element => {
    if (!open) return <></>
    const theme = useTheme()

    const { company = "WMS" } = useSelector((store: RootStateType) => store.navbarInfo)
    const { currencyCode = "INR" } = useSelector((store: RootStateType) => store.currencyInfo);
    const [mobileElementWidth, setMobileElementWidth] = useState(null);

    useEffect(() => {
        const mobileElement = document.getElementsByName('applicants.0.countryNameAndCode');
        setMobileElementWidth(mobileElement[0]?.parentElement?.clientWidth as any);
    }, []);

    const { nomineeDetails } = value;
    let currentNominee = {} as updateNominee;
    if (index !== -1) {
        currentNominee = nomineeDetails[index as number]
    }

    const {
        COUNTRY = [],
        STATE = [],
        CITY = [],
        RELATIONSHIP = [],
    } = mdmsCountriesList || {};

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '15px',
                    backgroundColor: 'background.default',
                    maxWidth: '900px',
                    padding: '25px',
                    m: 0,
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h2" sx={{ fontWeight: 600 }}>
                        Add Nominee
                    </Typography>
                    <IconButton onClick={onClose}>
                        <DialogCloseIcon />
                    </IconButton>
                </Box>
                <Formik
                    initialValues={index !== -1 ? currentNominee : newNominee}
                    validate={(values: updateNominee) => {
                        const mainValueObj = {
                            ...value
                        }
                        try {
                            validateYupSchema(values, addNomineeSchema(applications, currencyCode, company, mainValueObj), true, values);
                        } catch (e) {
                            return yupToFormErrors(e);
                        }
                    }}
                    onSubmit={(values, actions) => {
                        const currentDetails = value?.nomineeDetails || [];
                        if (index !== -1) {
                            currentDetails[index as number] = values;
                            setNomineeFieldValue(`nomineeDetails`, currentDetails);
                        } else {
                            setNomineeFieldValue(`nomineeDetails`, [...currentDetails, values]);
                        }
                        actions.resetForm();
                        onClose();
                    }}
                >
                    {({ values, setFieldValue, errors }) => (
                        <Form>
                            <Grid container columnSpacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <MFTextField name="nomineeName" label="Nominee Name" placeholder="Nominee Name" />
                                </Grid>
                                {/* <Grid item xs={12} sm={6}>
                  <MFSelectField
                    name="nomineeRelationship"
                    label="Nominee Relationship"
                    items={NomineeRelations.map(relation => ({ key: relation, value: relation }))}
                  />
                </Grid> */}
                                <Grid item xs={12} sm={6}>
                                    <MFSelectField
                                        name={`Relationship`}
                                        label="Select Nominee Relationship*"
                                        items={RELATIONSHIP.map((relation: dataTypeMasters) => ({
                                            key: relation?.description,
                                            value: relation?.description,
                                        }))}
                                        isCapitalize
                                    />
                                </Grid>
                                {(`${values.Relationship ?? ''}`.toLowerCase() === 'others') && (
                                    <Grid item xs={12} sm={6}>
                                        <MFTextField
                                            name={`nomineeRelationship`}
                                            label="Please Specify Relationship*"
                                            placeholder={`Enter Nominee Relationship`}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <TextDatePicker
                                        label={'Date of Birth *'}
                                        inputLabelStyles={{
                                            transform: 'unset',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: 'rgba(0,0,0,0.7)',
                                        }}
                                        placeholder={'Enter Date of Birth'}
                                        name={`dateOfBirth`}
                                        minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 125))}
                                    />
                                </Grid>
                                {/* <Grid item xs={12} sm={6}>
                  <MFTextField
                    name={`nomineePan`}
                    label={`Enter Nominee Pan`}
                    placeholder={`Enter Nominee Pan`}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    onChange={(e) => {
                      setFieldValue(`nomineePan`, e.target.value.toUpperCase());
                    }}
                  />
                </Grid> */}
                                {isMinor(values.dateOfBirth ? values.dateOfBirth : '') && (
                                    <>
                                        <Grid item xs={12} lg={6}>
                                            <MFTextField
                                                name={`guardianName`}
                                                label="Enter Guardian Name*"
                                                placeholder="Enter Guardian Name"
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={6}>
                                            <MFTextField
                                                name={`nomineeGuardianPan`}
                                                label={`Enter Guardian Pan*`}
                                                placeholder="Enter Guardian Pan"
                                                inputProps={{ style: { textTransform: 'uppercase' } }}
                                                onChange={(e) => {
                                                    setFieldValue(`nomineeGuardianPan`, e.target.value.toUpperCase());
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <MFSelectField
                                        name="idProofType"
                                        label="Any one of the id proof*"
                                        items={IdProofsMasters.map(proof => ({ key: proof, value: proof }))}
                                    />
                                </Grid>
                                {values.idProofType && (
                                    <Grid item xs={12} sm={6}>
                                        <MFTextField
                                            name={`idProofValue`}
                                            label={idProofLabelsMasters[values.idProofType]}
                                            placeholder={idProofLabelsMasters[values.idProofType]}
                                            inputProps={{ style: { textTransform: 'uppercase' } }}
                                            type={values.idProofType === 'Aadhaar Card' ? 'number' : 'text'}
                                            // onChange={(e) => {
                                            //     setFieldValue(`idProofValue`, e.target.value.toUpperCase());
                                            // }}
                                            onChange={(e) => {
                                                if (values.idProofType === 'Aadhaar Card' && e.target.value.length > 4) {
                                                    setFieldValue(`idProofValue`, e.target.value.slice(0, 4));
                                                } else {
                                                    setFieldValue(`idProofValue`, e.target.value.toUpperCase());
                                                }
                                            }}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <SubHeading
                                        sx={{
                                            letterSpacing: '0.02px',
                                        }}>
                                        Address & Contact Details
                                    </SubHeading>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <MFTextField
                                        name="mobile"
                                        label="Mobile*"
                                        placeholder="Mobile*"
                                        startAdornment={
                                            <CountryCodesDropDown
                                                ElementWidth={mobileElementWidth && mobileElementWidth}
                                                name={`countryNameAndCode`}
                                                value={values.countryNameAndCode}
                                            />
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MFTextField name="email" label="Email*" placeholder="Email*" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MFTextField name="address1" label="Address Line 1*" placeholder="Address Line 1*" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MFTextField name="address2" label="Address Line 2" placeholder="Address Line 2" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MFTextField name="address3" label="Address Line 3" placeholder="Address Line 3" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MFTextField name="pincode" label="PIN Code*" placeholder="PIN Code*" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <SearchableSelect
                                        name={`city`}
                                        label="City*"
                                        items={CITY.map((val: any) => ({
                                            key: val?.description,
                                            value: val?.description,
                                        }))}
                                        searchFieldPlaceholder={'Search City'}
                                    />
                                </Grid>
                                {`${values?.city ?? ''}`.toLowerCase() === 'others' && (
                                    <Grid item xs={12} sm={6}>
                                        <MFTextField name="otherCity" label="Enter City*" placeholder="Enter Other City" />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <SearchableSelect
                                        name={`state`}
                                        label="State*"
                                        items={STATE.map((val: any) => ({
                                            key: val?.description,
                                            value: val?.description,
                                        }))}
                                        searchFieldPlaceholder={'Search State'}
                                    />
                                </Grid>
                                {`${values?.state ?? ''}`.toLowerCase() === 'others' && (
                                    <Grid item xs={12} sm={6}>
                                        <MFTextField name="otherState" label="Enter State*" placeholder="Enter Other State" />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <SearchableSelect
                                        name={`country`}
                                        label="Country*"
                                        items={COUNTRY.map((val: any) => ({
                                            key: val?.description,
                                            value: val?.description,
                                        }))}
                                        searchFieldPlaceholder={'Search Country'}
                                    />
                                </Grid>
                            </Grid>

                            <DialogActions sx={{ pt: 4 }}>
                                <Grid item xs={12} sm={6}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        sx={{
                                            color: 'primary.500',
                                            backgroundColor: 'transparent',
                                            borderColor: 'primary.500',
                                            borderWidth: '0.5px',
                                            borderStyle: 'solid',
                                            padding: "10px",
                                            "&:hover, &.Mui-focusVisible": {
                                                borderColor: 'primary.600',
                                                backgroundColor: "background.default"
                                            },
                                            ...theme.typography.body_600
                                        }}
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid
                                    item xs={12} sm={6}>
                                    <Box mt={1} mb={1}>
                                        <MFSubmitButton label="Submit" disabled={false} loadingEnable={false} />
                                    </Box>
                                </Grid>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};